import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BaseGetTradeExtraInfoDataRequest,
  BotType,
  OpenMessage,
  PrecisionType,
  TimeRange,
  TradeDetailsSocketData,
} from "@shared/types";
import { SkipToken, skipToken } from "@reduxjs/toolkit/query";
import {
  useTradeChartAnnotations,
  useTradeChartData,
} from "@shared/features/trades/common/hooks";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { sharedLib } from "@shared/services/shared.service";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context";
import { TIME_RANGE_START } from "@shared/common/constants";
import { TradeSource } from "@shared/features/trades/common/types";
import useSocket from "@shared/features/socket/useSocket";
import { SocketTopics } from "@shared/types";
import { useRefValue } from "@shared/utils/hooks/misc";

interface TradeDetailsProviderProps extends PropsWithChildren {
  tradeId?: number;
  botId?: number;
  includeGraphData?: boolean;
  isBotDrawer?: boolean;
  isChannelConfiguration?: boolean;
  openMessage: OpenMessage;
  isBacktesting?: boolean;
  isEditTrade?: boolean;
}

const TradeDetailsProvider = ({
  tradeId: propsTradeId,
  botId,
  includeGraphData,
  isBotDrawer,
  isChannelConfiguration,
  openMessage,
  children,
  isBacktesting,
  isEditTrade,
}: TradeDetailsProviderProps) => {
  const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGE_START);
  const [socketOverrides, setSocketOverrides] = useState<
    TradeDetailsSocketData | undefined
  >(undefined);
  const socketOverridesTimestampRef = useRef<number | undefined>(undefined);
  const { getPrecision } = usePrecisions();

  const { currentData } = sharedLib.sharedApi.bots.useGetBotDataQuery(
    botId
      ? {
          dca_bot_id: botId,
          is_channel_configuration: isChannelConfiguration,
          is_backtesting: isBacktesting,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true }
  );
  const { currentData: botMetadata } =
    sharedLib.sharedApi.bots.useGetBotMetadataQuery(
      botId
        ? {
            dca_bot_id: botId,
            is_channel_configuration: isChannelConfiguration,
            is_backtesting: isBacktesting,
          }
        : skipToken,
      { refetchOnMountOrArgChange: true }
    );
  const botData = currentData?.data;
  const tradeId = botMetadata?.active_trade_id ?? propsTradeId;

  const baseArgs: BaseGetTradeExtraInfoDataRequest | SkipToken = useMemo(
    () =>
      tradeId
        ? {
            ignore_status: true,
            trade_id: tradeId,
            is_backtesting: isBacktesting,
          }
        : skipToken,
    [tradeId, isBacktesting]
  );

  const queryOptions = {
    skip: !tradeId,
    refetchOnMountOrArgChange: true,
  };

  const getTradeMetadataStatus =
    sharedLib.sharedApi.trades.useGetTradeMetadataQuery(baseArgs, queryOptions);
  const { data: tradeMetadata } = getTradeMetadataStatus;

  const getTradeExtraInfoOrdersStatus =
    sharedLib.sharedApi.trades.useGetTradeExtraInfoOrdersQuery(
      baseArgs,
      queryOptions
    );
  const { currentData: ordersData } = getTradeExtraInfoOrdersStatus;

  const isGridBotTrade =
    botMetadata?.bot_type === BotType.GRID ||
    tradeMetadata?.source === TradeSource.GRID_BOT;

  const getTradeExtraInfoGridsStatus =
    sharedLib.sharedApi.trades.useGetTradeExtraInfoGridsQuery(
      isGridBotTrade ? baseArgs : skipToken,
      queryOptions
    );
  const { data: gridCardsData } = getTradeExtraInfoGridsStatus;

  const getTradeExtraInfoStatsStatus =
    sharedLib.sharedApi.trades.useGetTradeExtraInfoStatsQuery(
      baseArgs,
      queryOptions
    );
  const { data: statsData, currentData: currentStatsData } =
    getTradeExtraInfoStatsStatus;

  const getTradeExtraInfoGeneralStatus =
    sharedLib.sharedApi.trades.useGetTradeExtraInfoGeneralQuery(
      botData ? skipToken : baseArgs,
      queryOptions
    );
  const { data: generalData, error } = getTradeExtraInfoGeneralStatus;
  const errorMessage = error?.data?.message;

  useEffect(() => {
    if (errorMessage) {
      openMessage(errorMessage, "error");
    }
  }, [errorMessage]);

  const getTradeExtraInfoActivitiesStatus =
    sharedLib.sharedApi.trades.useGetTradeExtraInfoActivitiesQuery(
      baseArgs,
      queryOptions
    );
  const { data: activities } = getTradeExtraInfoActivitiesStatus;

  const getTradeExtraInfoConfigurationStatus =
    sharedLib.sharedApi.trades.useGetTradeExtraInfoConfigurationQuery(
      botData ? skipToken : baseArgs,
      queryOptions
    );
  const { currentData: configurationData } =
    getTradeExtraInfoConfigurationStatus;

  const getSymbolAndExchange = () => {
    let exchange, symbol;
    if (botMetadata) {
      exchange = botMetadata.exchange;
      symbol = botMetadata.symbols?.[0];
    } else if (tradeMetadata) {
      exchange = tradeMetadata.exchange;
      symbol = tradeMetadata.symbol;
    }
    return [symbol, exchange];
  };
  const [symbol, exchange] = getSymbolAndExchange();
  const pricePrecision = getPrecision(PrecisionType.PRICE, symbol, exchange);
  const amountPrecision = getPrecision(PrecisionType.AMOUNT, symbol, exchange);

  const { getTradeExtraInfoOrdersGraphStatus, graphData, graphChangeValues } =
    useTradeChartData({
      tradeId,
      timeRange,
      activitiesArgs: baseArgs,
      skipGraphData: !includeGraphData,
    });

  const refetchOrdersData = () => {
    [
      getTradeExtraInfoActivitiesStatus,
      getTradeExtraInfoOrdersGraphStatus,
      getTradeExtraInfoOrdersStatus,
      getTradeExtraInfoStatsStatus,
    ].forEach((query) => {
      try {
        query.refetch();
      } catch {}
    });
  };

  const activeTradeId = currentStatsData?.id ?? tradeId;
  const isEditTradeRef = useRefValue(isEditTrade);
  const socketTradeId = currentStatsData?.trade_open
    ? activeTradeId
    : undefined;

  const tradeDetailsSocketArgs = useMemo(
    () => (socketTradeId ? { tradeId: socketTradeId } : skipToken),
    [socketTradeId]
  );
  const tradeDetailsSocketData = useSocket(
    SocketTopics.TRADE_EXTRA_DETAILS,
    tradeDetailsSocketArgs
  );

  const pendingSocketDataRef = useRef<TradeDetailsSocketData | undefined>(
    undefined
  );

  const applySocketOverrides = useCallback(
    (data: TradeDetailsSocketData, timestamp: number | undefined) => {
      setSocketOverrides(data);
      socketOverridesTimestampRef.current = timestamp;
      pendingSocketDataRef.current = undefined;
    },
    []
  );

  useEffect(() => {
    if (!tradeDetailsSocketData) return;
    const { value: socketValue, timestamp: socketTimestamp } =
      tradeDetailsSocketData;
    if (!socketValue) return;

    const socketData = socketValue.data;
    if (isEditTradeRef.current) {
      pendingSocketDataRef.current = socketData;
    } else {
      applySocketOverrides(socketData, socketTimestamp);
    }
  }, [tradeDetailsSocketData]);

  useEffect(() => {
    if (isEditTrade || getTradeExtraInfoStatsStatus.isFetching) return;
    const pendingSocketData = pendingSocketDataRef.current;
    if (!pendingSocketData) return;

    applySocketOverrides(pendingSocketData, tradeDetailsSocketData?.timestamp);
  }, [isEditTrade, getTradeExtraInfoStatsStatus.isFetching]);

  const actualOverrideData = shouldApplySocketOverrides(
    getTradeExtraInfoStatsStatus.fulfilledTimeStamp,
    socketOverridesTimestampRef.current
  )
    ? socketOverrides
    : undefined;

  const mergedOrdersData = actualOverrideData?.orders ?? ordersData;
  const mergedGeneralData = actualOverrideData?.general ?? generalData;
  const mergedActivities = actualOverrideData?.activities ?? activities;
  const mergedConfigurationData =
    actualOverrideData?.configuration ?? configurationData;
  const mergedStatsData = actualOverrideData?.stats ?? statsData;
  const mergedGridCardsData = actualOverrideData?.grids ?? gridCardsData;
  const mergedTradeMetadata = actualOverrideData?.metadata ?? tradeMetadata;

  const { activitiesAnnotations, tradeAnnotations } = useTradeChartAnnotations({
    ordersData: mergedOrdersData,
    activities: mergedActivities,
    botData,
    pricePrecision,
    gridCardsData: mergedGridCardsData,
    isOpenTrade: mergedTradeMetadata?.trade_open,
    isGridBotTrade,
  });

  return (
    <TradeDetailsContext.Provider
      value={{
        baseArgs,
        getTradeExtraInfoOrdersStatus: withMergedData(
          getTradeExtraInfoOrdersStatus,
          mergedOrdersData
        ),
        ordersData: mergedOrdersData,
        getTradeExtraInfoGeneralStatus: withMergedData(
          getTradeExtraInfoGeneralStatus,
          mergedGeneralData
        ),
        generalData: mergedGeneralData,
        getTradeExtraInfoActivitiesStatus: withMergedData(
          getTradeExtraInfoActivitiesStatus,
          mergedActivities
        ),
        activities: mergedActivities,
        getTradeExtraInfoConfigurationStatus: withMergedData(
          getTradeExtraInfoConfigurationStatus,
          mergedConfigurationData
        ),
        configurationData: mergedConfigurationData,
        getTradeExtraInfoStatsStatus: withMergedData(
          getTradeExtraInfoStatsStatus,
          mergedStatsData
        ),
        statsData: mergedStatsData,
        getTradeExtraInfoGridsStatus: withMergedData(
          getTradeExtraInfoGridsStatus,
          mergedGridCardsData
        ),
        gridCardsData: mergedGridCardsData,
        getTradeMetadataStatus: withMergedData(
          getTradeMetadataStatus,
          mergedTradeMetadata
        ),
        tradeMetadata: mergedTradeMetadata,
        currentTradeMetadata: mergedTradeMetadata,
        activitiesAnnotations,
        tradeAnnotations,
        symbol,
        exchange,
        refetchOrdersData,
        getTradeExtraInfoOrdersGraphStatus,
        graphData,
        timeRange,
        setTimeRange,
        graphChangeValues,
        pricePrecision,
        amountPrecision,
        isBotDrawer,
        isGridBotTrade,
        isBacktesting,
      }}
    >
      {children}
    </TradeDetailsContext.Provider>
  );
};

export default TradeDetailsProvider;

const shouldApplySocketOverrides = (
  lastFulfilled: number | undefined,
  socketTimestamp: number | undefined
): boolean =>
  !!lastFulfilled && !!socketTimestamp && socketTimestamp > lastFulfilled;

const withMergedData = <T,>(status: T, mergedData: unknown): T => ({
  ...status,
  data: mergedData,
  currentData: mergedData,
});
